import React from 'react';
import Typography from '@/components/atoms/typography/typography';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import DestinationFactsManualBox from './destination-facts-manual-box/destination-facts-manual-box';
import DestinationFactsManualTemperatureItem from './destination-facts-manual-temperature-item/destination-facts-manual-temperature-item';
import DestinationFactsManualTravelItem from './destination-facts-manual-travel-item/destination-facts-manual-travel-item';
import styles from './destination-facts-manual.module.scss';

export interface DestinationFactsManualProps extends ModuleWrapperProps {
  /** A list of boxes detailing the destination */
  boxes?: Content.Components['DestinationFactsManualModuleDto']['boxes'];
  /** The text to show as a headline */
  headline?: Content.Components['DestinationFactsManualModuleDto']['headline'];
  /** The link to show */
  link?: Content.Components['DestinationFactsManualModuleDto']['link'];
  /** A list of items detailing the temperature */
  temperatureItems?: Content.Components['DestinationFactsManualModuleDto']['temperatureItems'];
  /** A list of items detailing the travel */
  travelItems?: Content.Components['DestinationFactsManualModuleDto']['travelItems'];
}

const DestinationFactsManual = ({
  boxes = [],
  headline,
  link,
  temperatureItems = [],
  travelItems = [],
  ...rest
}: DestinationFactsManualProps) => {
  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container className={styles.container}>
        {headline && (
          <section>
            <Row>
              <Column>
                <Typography
                  className={styles.headline}
                  tag="h2"
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>
              </Column>
            </Row>
          </section>
        )}

        {temperatureItems.length > 0 && (
          <React.Fragment>
            <section className={styles.temperatureItems}>
              <Row className={styles.row}>
                {temperatureItems.map(
                  (temperatureItem, temperatureItemIndex) => (
                    <Column
                      key={temperatureItemIndex}
                      className={styles.column}
                      width={{ tablet: 4 }}
                    >
                      <DestinationFactsManualTemperatureItem
                        headline={temperatureItem.headline}
                        prefix={temperatureItem.preText}
                        temperature={temperatureItem.temperatureText}
                      />
                    </Column>
                  ),
                )}
              </Row>

              {link && (
                <Row className={styles.row}>
                  <Column className={styles.linkColumn}>
                    <AnchorLink icon="ArrowRight" {...link} />
                  </Column>
                </Row>
              )}
            </section>
          </React.Fragment>
        )}

        {temperatureItems.length > 0 && travelItems.length > 0 && (
          <section>
            <Row className={styles.row}>
              <Column>
                <div className={styles.divider} />
              </Column>
            </Row>
          </section>
        )}

        {travelItems.length > 0 && (
          <section>
            <Row className={styles.row}>
              {travelItems.map((travelItem, travelItemIndex) => (
                <Column
                  key={travelItemIndex}
                  className={styles.column}
                  width={{ tablet: 4 }}
                >
                  <DestinationFactsManualTravelItem
                    headline={travelItem.headline}
                    icon={travelItem.icon}
                    preHeadline={travelItem.preHeadline}
                  />
                </Column>
              ))}
            </Row>
          </section>
        )}

        {boxes.length > 0 && (
          <section>
            <Row className={styles.row}>
              {boxes.map((box, boxIndex) => (
                <Column
                  key={boxIndex}
                  className={styles.column}
                  width={{ laptop: 6 }}
                >
                  <DestinationFactsManualBox
                    headline={box.headline}
                    informationItems={
                      'informationItems' in box
                        ? box.informationItems
                        : undefined
                    }
                    link={box.link}
                    ratingItems={
                      'ratingItems' in box ? box.ratingItems : undefined
                    }
                  />
                </Column>
              ))}
            </Row>
          </section>
        )}
      </Container>
    </ModuleWrapper>
  );
};

DestinationFactsManual.displayName = 'DestinationFactsManual';

export default DestinationFactsManual;
